import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import AssetVersionSlotField from "../../components/AssetVersionSlotField";
import { FIXED_METADATA_SLOTS } from "../compositionSlots";
import { useResolvedAssetVersionContent } from "./useResolvedAssetVersionContent";

/**
 * EmailBuilderMetadata — EMAILS-IMPL-015, rendering fixed by EMAILS-IMPL-026
 *
 * The fixed email-level/destination metadata strip above the Canvas
 * (CRE-SPEC-001 §8.6): Subject, Preview Text, and Landing Page are never
 * Canvas blocks -- Subject/Preview are envelope/preheader-level metadata
 * never rendered inside the body, and Landing Page is governed
 * destination configuration, not body content. Reuses
 * AssetVersionSlotField unchanged (the exact same governed
 * Asset+exact-Version picker already established by EMAILS-IMPL-011) for
 * SELECTING the pin in Builder mode.
 *
 * EMAILS-IMPL-026: every slot now ALSO shows its actual resolved exact-
 * Version content (Subject/Preview canonical text; Landing Page's
 * destination) -- previously only Landing Page attempted this, and only
 * in the read-only summary; Subject/Preview never fetched or rendered
 * their own canonical text anywhere. Uses the SAME
 * `useResolvedAssetVersionContent` hook EmailBuilderCanvasBlock.jsx uses,
 * never a second content-resolution implementation.
 */
export default function EmailBuilderMetadata({ fingerprint, onChangeSlot, readOnly, mode }) {
  const showMeta = mode !== "preview";
  return (
    <Card className="glass border-white/10" data-testid="builder-metadata">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-slate-300">Email Metadata</CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-3 pt-0 sm:grid-cols-3">
        {FIXED_METADATA_SLOTS.map((slot) => (
          <div key={slot.key} className="space-y-2">
            {!readOnly && (
              <AssetVersionSlotField
                label={slot.label}
                required={slot.required}
                value={fingerprint[slot.key]}
                onChange={(value) => onChangeSlot(slot.key, value)}
                listFn={slot.listFn}
                versionsFn={slot.versionsFn}
                idField={slot.idField}
                eligibleStatuses={slot.eligibleStatuses}
                testPrefix={`builder-metadata-${slot.key}`}
              />
            )}
            <FixedSlotSummary slot={slot} value={fingerprint[slot.key]} showMeta={showMeta} />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function FixedSlotSummary({ slot, value, showMeta }) {
  const resolved = useResolvedAssetVersionContent(slot.versionsFn, value?.assetId, value?.version);

  if (!value) {
    return (
      <div className="rounded-md border border-white/10 bg-black/20 p-2 text-sm" data-testid={`builder-metadata-${slot.key}-summary`}>
        <div className="text-xs uppercase tracking-wide text-slate-500">{slot.label}</div>
        <div className="text-xs text-slate-500">—</div>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-white/10 bg-black/20 p-2 text-sm" data-testid={`builder-metadata-${slot.key}-summary`}>
      <div className="text-xs uppercase tracking-wide text-slate-500">{slot.label}</div>
      {showMeta && (
        <div className="font-mono text-xs text-slate-500" data-testid={`builder-metadata-${slot.key}-meta`}>
          {value.assetId} @ {value.version}
        </div>
      )}
      <SlotContent slot={slot} value={value} resolved={resolved} />
    </div>
  );
}

function SlotContent({ slot, value, resolved }) {
  if (resolved.status === "idle" || resolved.status === "loading") {
    return <div className="mt-1 text-xs text-slate-500">Loading content…</div>;
  }
  if (resolved.status === "unresolved") {
    return (
      <div className="mt-1 text-xs text-amber-300" data-testid={`builder-metadata-${slot.key}-unresolved`}>
        Unable to resolve {value.assetId} @ {value.version}
      </div>
    );
  }

  const content = resolved.content;

  if (slot.family === "LPG") {
    const destinationText = content.destination_url
      ? content.destination_url
      : content.internal_landing_page_id
      ? `Internal LP ${content.internal_landing_page_id} v${content.internal_landing_page_version}`
      : "No destination configured";
    return (
      <div className="mt-1 truncate text-xs text-slate-300" data-testid="builder-metadata-lpg-destination">
        {destinationText}
      </div>
    );
  }

  const field = { subject: "canonical_subject", preview: "canonical_preview" }[slot.key];
  const text = field && content[field];
  return text ? (
    <div className="mt-1 whitespace-pre-wrap text-sm text-slate-100" data-testid={`builder-metadata-${slot.key}-content`}>
      {text}
    </div>
  ) : (
    <div className="mt-1 text-xs text-amber-300" data-testid={`builder-metadata-${slot.key}-unresolved`}>
      Unable to resolve {value.assetId} @ {value.version}
    </div>
  );
}
